import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './UserPlansReport.css';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from "jspdf-autotable";

// Helper to safely get nested values with fallback
const safeGet = (obj, path, defaultValue = 'N/A') => {
  return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : null), obj) || defaultValue;
};

const UserPlansReport = () => {
  const [activeTab, setActiveTab] = useState('userPlan');
  const [userPlanReport, setUserPlanReport] = useState([]);
  const [genderWiseStats, setGenderWiseStats] = useState([]);

  // Filters state
  const [filters, setFilters] = useState({
    userId: '',
    gender: '',
    nameOrEmail: '',
    location: '',
    plan: '',
    status: '',
    paymentMethod: '',
  });

  // Fetch reports from API
  const fetchReports = async () => {
    try {
      const res = await axios.get('https://back.varankal.com/api/report/full-user-report');
      setUserPlanReport(res.data.userPlanReport || []);
      setGenderWiseStats(res.data.genderWiseStats || []);
    } catch (err) {
      console.error('Error fetching reports:', err);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  // Update filter values
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Apply filters to user plan data
  const filteredUserPlans = userPlanReport.filter((item) => {
    const { userId, gender, plan, status, payment } = item;
    const location = safeGet(item, 'location.state', '').toLowerCase() + safeGet(item, 'location.district', '').toLowerCase();
    const name = (item.name || '').toLowerCase();
    const email = (item.email || '').toLowerCase();
    const paymentMethod = safeGet(item, 'payment.method', '').toLowerCase();

    // Check userId filter (exact or partial)
    if (filters.userId && !userId.toLowerCase().includes(filters.userId.toLowerCase())) {
      return false;
    }

    // Gender filter
    if (filters.gender && filters.gender !== gender) {
      return false;
    }

    // Name or email search
    if (filters.nameOrEmail) {
      const term = filters.nameOrEmail.toLowerCase();
      if (!name.includes(term) && !email.includes(term)) {
        return false;
      }
    }

    // Location filter (search in state or district)
    if (filters.location) {
      if (!location.includes(filters.location.toLowerCase())) {
        return false;
      }
    }

    // Plan filter
    if (filters.plan && filters.plan !== (plan || 'Free')) {
      return false;
    }

    // Status filter
    if (filters.status && filters.status !== (status || 'N/A')) {
      return false;
    }

    // Payment Method filter
    if (filters.paymentMethod && filters.paymentMethod !== paymentMethod) {
      return false;
    }

    return true;
  });

  // Flatten object helper for CSV/Excel
  const flatten = (obj) => {
    const result = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
          for (const subKey in obj[key]) {
            if (obj[key].hasOwnProperty(subKey)) {
              result[`${key}_${subKey}`] = obj[key][subKey];
            }
          }
        } else {
          result[key] = obj[key];
        }
      }
    }
    return result;
  };

  // CSV download helper
  const escapeCSVValue = (value) => {
    if (value == null) return '';
    const stringValue = value.toString();
    if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const downloadCSV = (data, fileName) => {
    if (data.length === 0) {
      alert('No data to download.');
      return;
    }

    const headers = Object.keys(flatten(data[0]));
    const csvRows = [headers.join(',')];

    for (const row of data) {
      const flatRow = flatten(row);
      const values = headers.map((header) => escapeCSVValue(flatRow[header] ?? ''));
      csvRows.push(values.join(','));
    }

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = `${fileName}.csv`;
    a.click();
    window.URL.revokeObjectURL(a.href);
  };

  // Excel download
  const downloadExcel = (data, fileName) => {
    if (data.length === 0) {
      alert('No data to download.');
      return;
    }
    const worksheetData = data.map(flatten);
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  // PDF download
  const downloadPDF = (data, fileName, columns) => {
    if (data.length === 0) {
      alert('No data to download.');
      return;
    }

    const doc = new jsPDF();
    const headers = columns.map((col) => col.header);
    const rows = data.map((row) =>
      columns.map((col) => {
        const value = col.accessor(row);
        if (value === null || value === undefined) return 'N/A';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (value instanceof Date) return value.toLocaleDateString();
        return value.toString();
      })
    );

    // Build columnStyles object for alignment and width
    let columnStyles = {};
    headers.forEach((header, index) => {
      // Example: center align all headers, you can customize by header name
      columnStyles[index] = {
        halign: 'center', // horizontal alignment in cell
        cellWidth: 'wrap', // let cell wrap content, can be 'auto', 'wrap', or number (width in pt)
        fontSize: 7,
      };

      // For numeric columns or IDs, you might want right align
      if (header.toLowerCase().includes('id') || header.toLowerCase().includes('amount') || header.toLowerCase().includes('count') || header.toLowerCase().includes('views')) {
        columnStyles[index].halign = 'right';
      }
    });

    autoTable(doc, {
      head: [headers],
      body: rows,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [22, 160, 133], halign: 'center' },
      startY: 20,
      columnStyles,
      margin: { left: 14, right: 14 }
    });

    doc.text(fileName, 14, 15);
    doc.save(`${fileName}.pdf`);
  };

  // Columns config for user plan report PDF
  const userPlanColumns = [
    { header: 'User ID', accessor: (row) => row.userId || 'N/A' },
    { header: 'Name', accessor: (row) => row.name || 'N/A' },
    { header: 'Email', accessor: (row) => row.email || 'N/A' },
    { header: 'Phone Number', accessor: (row) => row.phoneNumber || 'N/A' },
    { header: 'Gender', accessor: (row) => row.gender || 'N/A' },
    { header: 'Occupation', accessor: (row) => row.occupation || 'N/A' },
    { header: 'Plan', accessor: (row) => row.plan || 'Free' },
    {
      header: 'Plan Start Date',
      accessor: (row) =>
        row.planStartDate ? new Date(row.planStartDate).toLocaleDateString() : 'N/A',
    },
    {
      header: 'Plan End Date',
      accessor: (row) =>
        row.planEndDate ? new Date(row.planEndDate).toLocaleDateString() : 'N/A',
    },
    { header: 'Status', accessor: (row) => row.status || 'N/A' },
    { header: 'Payment Method', accessor: (row) => safeGet(row, 'payment.method') },
    { header: 'Amount Paid', accessor: (row) => safeGet(row, 'payment.amountPaid', 0) },
    { header: 'Payment Status', accessor: (row) => safeGet(row, 'payment.planStatus') },
    { header: 'State', accessor: (row) => safeGet(row, 'location.state') },
    { header: 'District', accessor: (row) => safeGet(row, 'location.district') },
    { header: 'Max Views', accessor: (row) => safeGet(row, 'views.maxViews', 0) },
    { header: 'Views Left', accessor: (row) => safeGet(row, 'views.viewsLeft', 0) },
    { header: 'Used Views', accessor: (row) => safeGet(row, 'views.usedViews', 0) },
    {
      header: 'Carry Over Enabled',
      accessor: (row) => (safeGet(row, 'views.carryOverEnabled', false) ? 'Yes' : 'No'),
    },
    {
      header: 'Upgraded On',
      accessor: (row) =>
        row.payment?.upgradedOn ? new Date(row.payment.upgradedOn).toLocaleDateString() : 'N/A',
    },
  ];

  // Columns config for gender stats PDF
  const genderStatsColumns = [
    { header: 'Gender', accessor: (row) => row.gender || 'N/A' },
    { header: 'Plan', accessor: (row) => row.plan || 'N/A' },
    { header: 'User Count', accessor: (row) => row.userCount ?? 0 },
  ];

  // Render User Plan Report table
  const renderUserPlanTable = () => {
    const data = filteredUserPlans;

    return (
      <table>
        <thead>
          <tr>
            <th>User ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Phone Number</th>
            <th>Gender</th>
            <th>Occupation</th>
            <th>Plan</th>
            <th>Plan Start Date</th>
            <th>Plan End Date</th>
            <th>Status</th>
            <th>Payment Method</th>
            <th>Amount Paid</th>
            <th>Payment Status</th>
            <th>State</th>
            <th>District</th>
            <th>Max Views</th>
            <th>Views Left</th>
            <th>Used Views</th>
            <th>Carry Over Enabled</th>
            <th>Upgraded On</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan="20" style={{ textAlign: 'center' }}>
                No records found.
              </td>
            </tr>
          ) : (
            data.map((user) => (
              <tr key={user._id || Math.random()}>
                <td>{user.userId || 'N/A'}</td>
                <td>{user.name || 'N/A'}</td>
                <td>{user.email || 'N/A'}</td>
                <td>{user.phoneNumber || 'N/A'}</td>
                <td>{user.gender || 'N/A'}</td>
                <td>{user.occupation || 'N/A'}</td>
                <td>{user.plan || 'Free'}</td>
                <td>{user.planStartDate ? new Date(user.planStartDate).toLocaleDateString() : 'N/A'}</td>
                <td>{user.planEndDate ? new Date(user.planEndDate).toLocaleDateString() : 'N/A'}</td>
                <td>{user.status || 'N/A'}</td>
                <td>{safeGet(user, 'payment.method')}</td>
                <td>{safeGet(user, 'payment.amountPaid', 0)}</td>
                <td>{safeGet(user, 'payment.planStatus')}</td>
                <td>{safeGet(user, 'location.state')}</td>
                <td>{safeGet(user, 'location.district')}</td>
                <td>{safeGet(user, 'views.maxViews', 0)}</td>
                <td>{safeGet(user, 'views.viewsLeft', 0)}</td>
                <td>{safeGet(user, 'views.usedViews', 0)}</td>
                <td>{safeGet(user, 'views.carryOverEnabled', false) ? 'Yes' : 'No'}</td>
                <td>{user.payment?.upgradedOn ? new Date(user.payment.upgradedOn).toLocaleDateString() : 'N/A'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    );
  };

  // Render Gender Wise Stats table
  const renderGenderStatsTable = () => {
    const data = genderWiseStats;

    return (
      <table>
        <thead>
          <tr>
            <th>Gender</th>
            <th>Plan</th>
            <th>User Count</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan="3" style={{ textAlign: 'center' }}>
                No records found.
              </td>
            </tr>
          ) : (
            data.map((stat, idx) => (
              <tr key={idx}>
                <td>{stat.gender || 'N/A'}</td>
                <td>{stat.plan || 'N/A'}</td>
                <td>{stat.userCount ?? 0}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    );
  };

  // Get unique options for dropdowns dynamically
  const uniqueValues = (key) => {
    const set = new Set();
    userPlanReport.forEach((item) => {
      if (key.includes('.')) {
        // Nested key
        const val = safeGet(item, key);
        if (val) set.add(val);
      } else if (item[key]) {
        set.add(item[key]);
      }
    });
    return Array.from(set).filter((v) => v !== null && v !== undefined && v !== '');
  };

  return (
    <div className="user-plans-report-container">
      <div className="tabs">
        <button
          className={activeTab === 'userPlan' ? 'active' : ''}
          onClick={() => setActiveTab('userPlan')}
        >
          User Plan Report
        </button>
        <button
          className={activeTab === 'genderWiseStats' ? 'active' : ''}
          onClick={() => setActiveTab('genderWiseStats')}
        >
          Gender Wise Stats
        </button>
      </div>

      {activeTab === 'userPlan' && (
        <>
          <div className="filters-container">
            <input
              type="text"
              placeholder="Search by User ID"
              name="userId"
              value={filters.userId}
              onChange={handleFilterChange}
            />
            <input
              type="text"
              placeholder="Search by Name or Email"
              name="nameOrEmail"
              value={filters.nameOrEmail}
              onChange={handleFilterChange}
            />

            <select name="gender" value={filters.gender} onChange={handleFilterChange}>
              <option value="">All Genders</option>
              {uniqueValues('gender').map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Search Location (State/District)"
              name="location"
              value={filters.location}
              onChange={handleFilterChange}
            />

            <select name="plan" value={filters.plan} onChange={handleFilterChange}>
              <option value="">All Plans</option>
              {uniqueValues('plan').map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>

            <select name="status" value={filters.status} onChange={handleFilterChange}>
              <option value="">All Statuses</option>
              {uniqueValues('status').map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <select name="paymentMethod" value={filters.paymentMethod} onChange={handleFilterChange}>
              <option value="">All Payment Methods</option>
              {uniqueValues('payment.method').map((pm) => (
                <option key={pm} value={pm}>
                  {pm}
                </option>
              ))}
            </select>
          </div>

          <div className="buttons-container">
            <button onClick={() => downloadCSV(filteredUserPlans, 'UserPlansReport')}>
              Download CSV
            </button>
            <button onClick={() => downloadExcel(filteredUserPlans, 'UserPlansReport')}>
              Download Excel
            </button>
            <button onClick={() => downloadPDF(filteredUserPlans, 'UserPlansReport', userPlanColumns)}>
              Download PDF
            </button>
          </div>

          <div className="table-container">{renderUserPlanTable()}</div>
        </>
      )}

      {activeTab === 'genderWiseStats' && (
        <>
          <div className="buttons-container">
            <button onClick={() => downloadCSV(genderWiseStats, 'GenderWiseStats')}>
              Download CSV
            </button>
            <button onClick={() => downloadExcel(genderWiseStats, 'GenderWiseStats')}>
              Download Excel
            </button>
            <button
              onClick={() => downloadPDF(genderWiseStats, 'GenderWiseStats', genderStatsColumns)}
            >
              Download PDF
            </button>
          </div>

          <div className="table-container">{renderGenderStatsTable()}</div>
        </>
      )}
    </div>
  );
};

export default UserPlansReport;
